import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { Box, Card, CardContent, IconButton, Typography } from '@mui/material'
import React from 'react'
import { CONTENT_CARD_WIDTH } from '../../../../components/general-card'
import { useFormFactor } from '../../../../hooks'
import { useScrollableRails } from '../hooks/useScrollableRails'
import { HomeRailsScrollableContainer } from './home-rails-scrollable-container'

// For Skill Bite and AI Skills Practice cards, which have no width of their
// own and fill the slot
export const WIDE_RAIL_SLOT_WIDTH = 300

// `itemWidth` must match the card renderItem returns: a wider slot adds its
// slack to the 16px gap between cards. The default fits content cards: it is
// BaseContentCard's cap on desktop, and below lg (where the card caps at 327)
// the slot is what sizes it.
export const HomeRails = ({
  title,
  items,
  renderItem,
  exploreMoreCta,
  dataTest,
  itemWidth = CONTENT_CARD_WIDTH
}) => {
  const { isMobile } = useFormFactor()
  const {
    scrollContainerRef,
    canScrollLeft,
    canScrollRight,
    hasScrollableContent,
    scrollLeft,
    scrollRight,
    updateScrollState
  } = useScrollableRails(items)

  const renderTitle = typeof title === 'string'
    ? <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
    : title

  const showControls = !isMobile && (exploreMoreCta || hasScrollableContent)

  return (
    <Card data-test={dataTest} sx={{ backgroundColor: (theme) => theme.palette.grey.shade[3] }}>
      <CardContent sx={{ m: 0, p: 0, '&:last-child': { pb: 0 } }}>
        {/* The "See all" link and the arrows share the title's row rather than
            a row of their own, which left an empty band above the cards. */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, p: 2, backgroundColor: (theme) => theme.palette.grey.shade[7], borderBottom: '1px solid #E0E0E0' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>{renderTitle}</Box>
          {showControls && (
            // The negative margin lets the 34px arrows overhang the title's
            // 21px line into the header padding, so the header is the same
            // height whether or not they show.
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, my: -1 }}>
              {exploreMoreCta && <Box>{exploreMoreCta}</Box>}
              {hasScrollableContent && (
                <Box display='flex' alignItems='center' justifyContent='end' data-test={`${dataTest}-scroll-buttons-container`}>
                  {/* Left scroll arrow */}
                  <IconButton
                    onClick={scrollLeft}
                    size='small'
                    data-test={`${dataTest}-scroll-left-button`}
                    disabled={!canScrollLeft}
                    aria-label='Scroll left to view previous items'
                  >
                    <ChevronLeft />
                  </IconButton>

                  {/* Right scroll arrow */}
                  <IconButton
                    onClick={scrollRight}
                    size='small'
                    data-test={`${dataTest}-scroll-right-button`}
                    disabled={!canScrollRight}
                    aria-label='Scroll right to view more items'
                  >
                    <ChevronRight />
                  </IconButton>
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ position: 'relative', pt: 2 }}>

          {/* Horizontal scrollable content */}
          <HomeRailsScrollableContainer
            scrollContainerRef={scrollContainerRef}
            onScroll={updateScrollState}
            dataTest={`${dataTest}-scrollable-container`}
          >
            {items.map((item, itemIndex) => (
              <Box
                key={item?.sk || itemIndex}
                sx={{
                  minWidth: itemWidth,
                  maxWidth: itemWidth,
                  flexShrink: 0
                }}
              >
                {renderItem(item, itemIndex)}
              </Box>
            ))}
          </HomeRailsScrollableContainer>
          {isMobile && (
            <Box sx={{ mt: 2, px: 2, pb: 2 }}>
              {exploreMoreCta}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  )
}
